/**
 * useQueueCallControl Hook
 *
 * Exposes hold/unhold/hangup for the call overlay via POST /app/call/control.
 * Hold state is tracked locally and reset when the call ends (enabled becomes false).
 */

import { useEffect, useState, useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';

import { queueRepository } from '../../infrastructure/repositories';

import type { CallControlAction } from '../../domain/types';

// ============================================================================
// Hook
// ============================================================================

export interface UseQueueCallControlOptions {
  /** When false (no active call), hold state is reset to false. */
  enabled: boolean;
}

export function useQueueCallControl(options: UseQueueCallControlOptions) {
  const { enabled } = options;
  const [isOnHold, setIsOnHold] = useState(false);

  const mutation = useMutation({
    mutationFn: (action: CallControlAction) => queueRepository.controlCall(action),
    onSuccess: (_data, action) => {
      if (action === 'hold') {
        setIsOnHold(true);
      } else if (action === 'unhold') {
        setIsOnHold(false);
      }
    },
  });

  const toggleHold = useCallback(() => {
    const action: CallControlAction = isOnHold ? 'unhold' : 'hold';
    mutation.mutate(action);
  }, [isOnHold, mutation]);

  const hangup = useCallback(() => {
    mutation.mutate('hangup');
  }, [mutation]);

  useEffect(() => {
    if (!enabled) {
      setIsOnHold(false);
    }
  }, [enabled]);

  return {
    isOnHold,
    toggleHold,
    hangup,
    isPending: mutation.isPending,
  };
}

/** @deprecated Use useQueueCallControl instead */
export const useQueueHoldControl = useQueueCallControl;

export type { UseQueueCallControlOptions as UseQueueHoldControlOptions };
